import React, { useState, useEffect, useRef, useCallback } from 'react';
import FirmaCanvas from '../components/pazienti/FirmaCanvas';
import intestazioniTablet from '../utils/chiaveDispositivo';
import '../styles/Tablet.css';
import ModulisticaWizard from '../components/pazienti/ModulisticaWizard';
import TimbraturaTablet from '../components/TimbraturaTablet';
import QuestionarioTablet from '../components/QuestionarioTablet';

/**
 * Tablet.jsx — la pagina che gira sul tablet in sala d'attesa.
 *
 * NON usa il Layout del gestionale e non ha alcun collegamento verso
 * altre pagine: il dispositivo resta in mano ai pazienti, e da qui non
 * si deve poter arrivare da nessun'altra parte.
 *
 * Si identifica con la chiave del dispositivo, salvata nel browser una
 * volta sola. Non c'e' nome utente, non c'e' accesso: il tablet vede
 * una sessione alla volta e per pochi minuti.
 */

const CHIAVE_SALVATA = 'rimedichub_dispositivo';
const INTERVALLO_MS = 4000;

const dataIta = (v) => {
    if (!v) return '';
    const d = new Date(v);
    return isNaN(d) ? '' : d.toLocaleDateString('it-IT');
};

/* Sostituzione dei segnaposto {{ }} nel testo del modello */
const costruisciMappa = (s) => ({
    'paziente.nome': s.paziente_nome || '',
    'paziente.cognome': s.paziente_cognome || '',
    'paziente.nome_completo': `${s.paziente_nome || ''} ${s.paziente_cognome || ''}`.trim(),
    'paziente.codice_fiscale': s.codice_fiscale || '',
    'paziente.data_nascita': dataIta(s.data_nascita),
    'paziente.luogo_nascita': s.luogo_nascita || '',
    'paziente.indirizzo': s.indirizzo || '',
    'paziente.citta': s.citta || '',
    'paziente.cap': s.cap || '',
    'paziente.provincia': s.provincia || '',
    'paziente.telefono': s.telefono || '',
    'paziente.email': s.email || '',
    'medico.nome': s.medico?.nome || '',
    'medico.cognome': s.medico?.cognome || '',
    'medico.titolo': s.medico?.titolo || '',
    'medico.nome_completo': s.medico
        ? `${s.medico.titolo || ''} ${s.medico.nome || ''} ${s.medico.cognome || ''}`.trim() : '',
    'data.oggi': new Date().toLocaleDateString('it-IT'),
});

const sostituisci = (testo, mappa) =>
    String(testo || '').replace(/\{\{\s*([\w.]+)\s*\}\}/g, (intero, chiave) =>
        mappa[chiave] !== undefined ? mappa[chiave] : intero);

/* Grassetto e corsivo del modello */
const conFormattazione = (testo) => {
    const pezzi = String(testo).split(/(\*\*[^*]+\*\*|_[^_]+_)/g).filter((p) => p !== '');
    return pezzi.map((pezzo, i) => {
        if (pezzo.startsWith('**') && pezzo.endsWith('**')) {
            return <b key={i}>{pezzo.slice(2, -2)}</b>;
        }
        if (pezzo.startsWith('_') && pezzo.endsWith('_')) {
            return <i key={i}>{pezzo.slice(1, -1)}</i>;
        }
        return <span key={i}>{pezzo}</span>;
    });
};

const Tablet = () => {
    const [chiave, setChiave] = useState(() => localStorage.getItem(CHIAVE_SALVATA) || '');
    const [chiaveInserita, setChiaveInserita] = useState('');
    const [struttura, setStruttura] = useState('');

    /* Come si chiama questa clinica lo dice il server: qui non sta piu'
       scritto. La rotta non e' autenticata perche' la usa anche il tablet,
       dove nessun utente e' collegato. */
    useEffect(() => {
        fetch('/api/struttura')
            .then((r) => r.json())
            .then((d) => setStruttura(d?.data?.ragione_sociale || ''))
            .catch(() => {});
    }, []);

    const [dispositivo, setDispositivo] = useState(null);
    const [sessione, setSessione] = useState(null);
    const [risposte, setRisposte] = useState({});
    const [firmaPaziente, setFirmaPaziente] = useState(null);
    const [firmaMedico, setFirmaMedico] = useState(null);
    const [passo, setPasso] = useState('lettura');   // lettura | firma | medico | fatto
    const [errore, setErrore] = useState(null);
    const [invio, setInvio] = useState(false);
    const codiceCorrente = useRef(null);

    // Modulo anagrafico: 'no' = non aperto
    const [anagrafica, setAnagrafica] = useState('no');
    /* La timbratura vive accanto all'accettazione, non dentro: sono due
       lavori diversi fatti dalle stesse persone sullo stesso apparecchio.
       Se il dispositivo non e' abilitato, il pulsante c'e' ma il server
       risponde no - e il componente lo dice. */
    const [timbratura, setTimbratura] = useState(false);
    const [questionarioFatto, setQuestionarioFatto] = useState(null);
    // 'completo' | 'prelievi': al punto prelievi non serve il consenso
    // alle immagini, e la segreteria deve saperlo dall'inizio.
    const [tipoAccettazione, setTipoAccettazione] = useState('completo');

    /* ---------- interrogazione periodica ---------- */
    const controlla = useCallback(async () => {
        if (!chiave) return;
        try {
            const r = await fetch('/api/firma-tablet/attesa',
                                  { headers: intestazioniTablet(chiave) });
            const d = await r.json();

            if (!d.success) {
                setErrore(d.message || 'Dispositivo non riconosciuto');
                if (r.status === 401) {
                    localStorage.removeItem(CHIAVE_SALVATA);
                    setChiave('');
                }
                return;
            }

            setErrore(null);
            setDispositivo(d.data.dispositivo);

            const nuova = d.data.sessione;

            // Nessun documento: si torna in attesa, ma non si cancella
            // quello appena firmato mentre il messaggio e' a video.
            if (!nuova) {
                if (codiceCorrente.current && passo !== 'fatto') {
                    codiceCorrente.current = null;
                    setSessione(null);
                }
                return;
            }

            // Documento nuovo: si azzera tutto
            if (nuova.codice !== codiceCorrente.current) {
                codiceCorrente.current = nuova.codice;
                setSessione(nuova);
                setRisposte({});
                setFirmaPaziente(null);
                setFirmaMedico(null);
                setPasso('lettura');
            }
        } catch (e) {
            console.error('Interrogazione:', e);
        }
    }, [chiave, passo]);

    useEffect(() => {
        if (!chiave) return;
        controlla();
        const t = setInterval(controlla, INTERVALLO_MS);
        return () => clearInterval(t);
    }, [chiave, controlla]);

    /* ---------- configurazione iniziale ---------- */
    if (!chiave) {
        return (
            <div className="tab-configurazione">
                <div className="tab-riquadro">
                    <h1>Configurazione del dispositivo</h1>
                    <p>
                        Inserisci la chiave che ti è stata fornita. Serve una volta sola:
                        resta salvata su questo apparecchio.
                    </p>
                    <input
                        type="password"
                        value={chiaveInserita}
                        onChange={(e) => setChiaveInserita(e.target.value)}
                        placeholder="Chiave del dispositivo"
                        autoFocus
                    />
                    {errore && <p className="tab-errore">{errore}</p>}
                    <button
                        onClick={() => {
                            const c = chiaveInserita.trim();
                            if (!c) return;
                            localStorage.setItem(CHIAVE_SALVATA, c);
                            setChiave(c);
                            setChiaveInserita('');
                        }}
                        disabled={!chiaveInserita.trim()}
                    >
                        Attiva
                    </button>
                </div>
            </div>
        );
    }

    /* ---------- documento firmato ---------- */
    if (passo === 'fatto') {
        return (
            <div className="tab-esito">
                <div className="tab-segno">✓</div>
                <h1>Grazie</h1>
                <p>Il documento è stato firmato e archiviato.</p>
                <p className="tab-minuto">Può riconsegnare il dispositivo alla segreteria.</p>
            </div>
        );
    }

    /* ---------- questionario consegnato ---------- */
    if (questionarioFatto) {
        return (
            <div className="tab-esito">
                <div className="tab-segno">✓</div>
                <h1>Grazie</h1>
                <p>Il questionario è stato inviato.</p>
                <p className="tab-minuto">
                    {questionarioFatto.risposte} risposte su {questionarioFatto.totale}.
                    Può riconsegnare il dispositivo alla segreteria.
                </p>
            </div>
        );
    }

    /* ---------- anagrafica inviata ---------- */
    if (anagrafica === 'fatto') {
        return (
            <div className="tab-esito">
                <div className="tab-segno">✓</div>
                <h1>Grazie</h1>
                <p>I moduli firmati sono stati inviati alla segreteria.</p>
                <p className="tab-minuto">Può riconsegnare il dispositivo.</p>
            </div>
        );
    }

    /* ---------- timbratura ---------- */
    if (timbratura) {
        return <TimbraturaTablet chiave={chiave} onChiudi={() => setTimbratura(false)} />;
    }

    /* ---------- accettazione: il percorso completo ---------- */
    if (anagrafica === 'si') {
        return (
            <ModulisticaWizard
                chiaveTablet={chiave}
                modalita={tipoAccettazione === 'prelievi' ? 'prelievi' : 'completo'}
                onClose={() => setAnagrafica('no')}
                onSuccess={() => setAnagrafica('fatto')}
            />
        );
    }

    /* ---------- pratica di un paziente gia' in archivio ----------
       Non c'e' un documento da firmare: c'e' l'anagrafica da far
       verificare e i quattro moduli, cioe' lo stesso percorso
       dell'accettazione ma su un paziente che esiste. */
    if (sessione && sessione.tipo_sessione === 'pratica' && sessione.paziente) {
        return (
            <ModulisticaWizard
                /* LA `key` LEGATA ALLA SESSIONE.
                   Senza, React riusa la stessa istanza quando arriva una
                   sessione nuova: lo stato nato da useState resta quello
                   del paziente di prima, mentre il codice della sessione
                   arriva dalle props ed e' gia' quello nuovo. I dati e le
                   firme di A finivano nella cartella di B. Con la `key`
                   il componente si smonta e ricomincia da zero. */
                key={sessione.codice}
                chiaveTablet={chiave}
                codiceSessione={sessione.codice}
                pazienteEsistente={sessione.paziente}
                modalita={sessione.paziente.tipo_accettazione === 'prelievi'
                    ? 'prelievi' : 'completo'}
                onClose={() => { codiceCorrente.current = null; setSessione(null); }}
                onSuccess={() => {
                    codiceCorrente.current = null;
                    setSessione(null);
                    setPasso('fatto');
                    setTimeout(() => setPasso('lettura'), 8000);
                }}
            />
        );
    }

    /* ---------- questionario ----------
       Una sessione di questionario non ha blocchi da leggere ne' una firma
       da raccogliere: e' un'altra cosa, e si comporta da un'altra parte.
       Il ramo sta qui sopra l'attesa perche' sotto ci sono i rami del
       documento, che darebbero per scontato `sessione.contenuto`. */
    if (sessione && sessione.tipo_sessione === 'questionario') {
        return (
            <QuestionarioTablet
                /* come sopra: una sessione nuova, un componente nuovo */
                key={sessione.codice}
                chiave={chiave}
                sessione={sessione}
                onFatto={(esito) => {
                    setQuestionarioFatto(esito);
                    codiceCorrente.current = null;
                    setSessione(null);
                    setTimeout(() => setQuestionarioFatto(null), 8000);
                }}
            />
        );
    }

    /* ---------- attesa ---------- */
    if (!sessione) {
        return (
            <div className="tab-attesa">
                <h1>{struttura || '\u00a0'}</h1>
                <p>Nessun documento da firmare al momento.</p>
                <div className="tab-accettazioni">
                    <button className="tab-nuovo"
                            onClick={() => { setTipoAccettazione('completo'); setAnagrafica('si'); }}>
                        Accettazione paziente
                    </button>
                    <button className="tab-nuovo"
                            onClick={() => { setTipoAccettazione('prelievi'); setAnagrafica('si'); }}>
                        Accettazione punto prelievi
                    </button>
                    <button className="tab-nuovo tab-nuovo--timbra"
                            onClick={() => setTimbratura(true)}>
                        Timbra entrata / uscita
                    </button>
                </div>
                {errore && <p className="tab-errore">{errore}</p>}
                <p className="tab-minuto">{dispositivo}</p>
            </div>
        );
    }

    /* ---------- documento ---------- */
    const mappa = costruisciMappa(sessione);
    const blocchi = sessione.contenuto?.contenuto?.blocchi || [];

    const obbligatoriMancanti = blocchi
        .map((b, i) => ({ b, i }))
        .filter(({ b, i }) => b.tipo === 'consenso' && b.obbligatorio && risposte[i] !== true);

    const invia = async () => {
        try {
            setErrore(null);
            setInvio(true);
            const r = await fetch('/api/firma-tablet/firma', {
                method: 'POST',
                headers: intestazioniTablet(chiave, true),
                body: JSON.stringify({
                    codice: sessione.codice,
                    risposte,
                    firma_base64: firmaPaziente,
                    firma_medico_base64: firmaMedico,
                }),
            });
            const d = await r.json();
            if (!d.success) {
                setErrore(d.message || 'Errore nella firma');
                return;
            }
            setPasso('fatto');
            codiceCorrente.current = null;
            // Il messaggio resta a video qualche secondo, poi si torna
            // in attesa da soli: nessuno deve toccare niente.
            setTimeout(() => {
                setSessione(null);
                setPasso('lettura');
            }, 6000);
        } catch (e) {
            setErrore('Errore di collegamento. Riprovare.');
        } finally {
            setInvio(false);
        }
    };

    return (
        <div className="tab-documento">
            <div className="tab-testata">
                <span className="tab-titolo">{sessione.titolo}</span>
                <span className="tab-paziente">
                    {sessione.paziente_nome} {sessione.paziente_cognome}
                </span>
            </div>

            <div className="tab-corpo">
                {passo === 'lettura' && (
                    <>
                        {blocchi.map((b, i) => {
                            if (b.tipo === 'sezione') {
                                return <h2 key={i} className="tab-sezione">
                                    {sostituisci(b.testo, mappa)}
                                </h2>;
                            }
                            if (b.tipo === 'paragrafo') {
                                return <p key={i} className="tab-paragrafo">
                                    {conFormattazione(sostituisci(b.testo, mappa))}
                                </p>;
                            }
                            if (b.tipo === 'elenco') {
                                const Lista = b.ordinato ? 'ol' : 'ul';
                                return (
                                    <Lista key={i} className="tab-elenco">
                                        {(b.voci || []).map((v, k) => (
                                            <li key={k}>{conFormattazione(sostituisci(v, mappa))}</li>
                                        ))}
                                    </Lista>
                                );
                            }
                            if (b.tipo === 'consenso') {
                                const scelto = risposte[i];
                                return (
                                    <div key={i} className={`tab-consenso ${b.obbligatorio ? 'obbligatorio' : ''}`}>
                                        <p className="tab-consenso-testo">
                                            {sostituisci(b.etichetta, mappa)}
                                            {b.obbligatorio && <span className="tab-richiesto"> — richiesto</span>}
                                        </p>
                                        {b.descrizione && (
                                            <p className="tab-consenso-nota">{sostituisci(b.descrizione, mappa)}</p>
                                        )}
                                        <div className="tab-scelte">
                                            <button
                                                className={scelto === true ? 'scelto si' : ''}
                                                onClick={() => setRisposte({ ...risposte, [i]: true })}
                                            >
                                                Acconsento
                                            </button>
                                            <button
                                                className={scelto === false ? 'scelto no' : ''}
                                                onClick={() => setRisposte({ ...risposte, [i]: false })}
                                            >
                                                Non acconsento
                                            </button>
                                        </div>
                                    </div>
                                );
                            }
                            return null;
                        })}

                        <div className="tab-azioni">
                            {obbligatoriMancanti.length > 0 && (
                                <p className="tab-avviso">
                                    Per proseguire occorre rispondere ai punti contrassegnati
                                    come richiesti.
                                </p>
                            )}
                            <button
                                className="tab-avanti"
                                onClick={() => setPasso('firma')}
                                disabled={obbligatoriMancanti.length > 0}
                            >
                                Ho letto, procedo alla firma
                            </button>
                        </div>
                    </>
                )}

                {passo === 'firma' && (
                    <div className="tab-firma">
                        <h2>Firma del paziente</h2>
                        <p className="tab-minuto">
                            {sessione.paziente_nome} {sessione.paziente_cognome} — firmi nello
                            spazio sottostante.
                        </p>
                        <FirmaCanvas valore={firmaPaziente} onChange={setFirmaPaziente} />
                        {errore && <p className="tab-errore">{errore}</p>}
                        <div className="tab-azioni">
                            <button className="tab-indietro" onClick={() => setPasso('lettura')}>
                                Torna al testo
                            </button>
                            <button
                                className="tab-avanti"
                                onClick={() => setPasso('medico')}
                                disabled={!firmaPaziente}
                            >
                                Avanti
                            </button>
                        </div>
                    </div>
                )}

                {passo === 'medico' && (
                    <div className="tab-firma">
                        <h2>Firma del medico</h2>
                        <p className="tab-minuto">
                            Consegnare il dispositivo al medico
                            {sessione.medico
                                ? ` — ${sessione.medico.titolo || ''} ${sessione.medico.cognome || ''}`
                                : ''}.
                        </p>
                        <FirmaCanvas valore={firmaMedico} onChange={setFirmaMedico} />
                        {errore && <p className="tab-errore">{errore}</p>}
                        <div className="tab-azioni">
                            <button className="tab-indietro" onClick={() => setPasso('firma')}>
                                Indietro
                            </button>
                            <button className="tab-avanti" onClick={invia} disabled={invio}>
                                {invio ? 'Invio…' : 'Concludi e archivia'}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Tablet;
